import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CurrencyDollar, Gauge, User, IdentificationCard, Phone, CheckCircle, Lightning, ShieldCheck, Percent, EnvelopeSimple, Plug, PlugsConnected, X as XIcon, Warning, ArrowSquareOut, Key, Sparkle } from "@phosphor-icons/react";
import client from "../lib/api";
import PilotDigestCard from "./PilotDigestCard";
import SlackWebhookSection from "../components/SlackWebhookSection";
import GmailConnectCard from "../components/GmailConnectCard";

const AGGRESSIVENESS_OPTIONS = [
  {
    value: "conservative",
    label: "Conservative",
    desc: "Anchor +5% over target. Close deals fast, leave a bit on the table.",
    icon: ShieldCheck,
    color: "text-emerald-400",
  },
  {
    value: "balanced",
    label: "Balanced",
    desc: "Anchor +8% over target. Best of both worlds — good rates, decent close rate.",
    icon: Gauge,
    color: "text-[#FF5A1F]",
  },
  {
    value: "aggressive",
    label: "Aggressive",
    desc: "Anchor +12% over target. Chase max rate — some brokers walk, others pay.",
    icon: Lightning,
    color: "text-amber-400",
  },
];

export default function PricingPrefsPage() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/settings/pricing");
        setPrefs(data);
      } catch (e) {
        toast.error("Failed to load pricing preferences");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await client.put("/settings/pricing", {
        default_uplift_usd: Number(prefs.default_uplift_usd) || 0,
        default_uplift_pct: Number(prefs.default_uplift_pct) || 0,
        min_per_mile: Number(prefs.min_per_mile) || 0,
        aggressiveness: prefs.aggressiveness || "balanced",
        mpg: Number(prefs.mpg) || 6.5,
        speculation_mode: !!prefs.speculation_mode,
        signature_name: prefs.signature_name || "",
        mc_number: prefs.mc_number || "",
        dot_number: prefs.dot_number || "",
        phone: prefs.phone || "",
      });
      // Once saved, this pilot is no longer a "first-run" — clear the nudge state
      setPrefs((p) => ({ ...p, mpg_source: "user" }));
      toast.success("Pricing rules saved. AI will use these on every negotiation.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) {
    return <div className="text-neutral-500 text-sm py-6 text-center">Loading…</div>;
  }

  const set = (k) => (e) => setPrefs({ ...prefs, [k]: e.target.value });

  return (
    <div className="max-w-3xl space-y-6" data-testid="pricing-prefs-page">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/30 bg-[#FF5A1F]/10 text-[#FF5A1F] px-3 py-1 text-xs font-semibold mb-3">
          <CurrencyDollar size={12} weight="fill" /> RATE NEGOTIATION RULES
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">Pricing preferences</h1>
        <p className="text-neutral-400 mt-2 text-sm sm:text-base">
          Set your defaults once. FreightPilot AI will use these rules on every broker email — automatically calculating your target rate, anchor, and professional justifications.
        </p>
      </div>

      {/* Gmail connection (SMTP + App Password) — shared card */}
      <GmailConnectCard />

      {/* Weekly business brief — pilot-facing opt-in + preview + history */}
      <PilotDigestCard />

      {/* Uplift rules */}
      <div className="card-surface p-6" data-testid="pricing-uplift-card">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">Target Uplift (AI uses whichever is higher)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-neutral-400 mb-1">Flat dollar uplift over posted rate</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">$</span>
              <input
                type="number"
                min={0}
                step={25}
                value={prefs.default_uplift_usd}
                onChange={set("default_uplift_usd")}
                data-testid="pricing-uplift-usd"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 pl-8 pr-3 focus:outline-none focus:border-[#FF5A1F]/60"
              />
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">Example: posted $2,000 + $250 = target $2,250</div>
          </div>
          <div>
            <label className="block text-xs text-neutral-400 mb-1">Percent uplift over posted rate</label>
            <div className="relative">
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                value={prefs.default_uplift_pct}
                onChange={set("default_uplift_pct")}
                data-testid="pricing-uplift-pct"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 pl-3 pr-8 focus:outline-none focus:border-[#FF5A1F]/60"
              />
              <Percent size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">Example: posted $2,000 × 1.15 = target $2,300</div>
          </div>
        </div>
      </div>

      {/* Minimum rate */}
      <div className="card-surface p-6" data-testid="pricing-min-card">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">Auto-Decline Threshold</div>
        <label className="block text-xs text-neutral-400 mb-1">Minimum rate per mile ($/mi)</label>
        <div className="relative max-w-xs">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">$</span>
          <input
            type="number"
            min={0}
            step={0.05}
            value={prefs.min_per_mile}
            onChange={set("min_per_mile")}
            data-testid="pricing-min-per-mile"
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 pl-8 pr-3 focus:outline-none focus:border-[#FF5A1F]/60"
          />
        </div>
        <p className="text-[11px] text-neutral-500 mt-2">
          Any load below this rate/mile triggers a warning. You can still send the email, but AI will flag it as a bad deal.
        </p>
      </div>

      {/* Truck MPG — powers Fuel Cost Estimate + Pilot Weekly Digest fuel narrative.
          When the pilot has never saved an MPG (mpg_source === "default"), we
          highlight the field with an orange ring + first-run tooltip to nudge
          them to set their real number. */}
      <div
        className={`card-surface p-6 transition-all ${
          prefs.mpg_source === "default" ? "ring-2 ring-[#FF5A1F]/40" : ""
        }`}
        data-testid="pricing-mpg-card"
      >
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Truck Fuel Economy</div>
          {prefs.mpg_source === "default" && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.08] px-2.5 py-0.5 text-[10px] font-semibold text-[#FF5A1F] tracking-wider"
              data-testid="pricing-mpg-first-run-badge"
            >
              <Sparkle size={10} weight="fill" />
              SET ME
            </span>
          )}
        </div>
        <label className="block text-xs text-neutral-400 mb-1">Average MPG (loaded)</label>
        <div className="relative max-w-xs">
          <input
            type="number"
            min={3.0}
            max={15.0}
            step={0.1}
            value={prefs.mpg ?? 6.5}
            onChange={set("mpg")}
            data-testid="pricing-mpg"
            className={`w-full bg-white/[0.03] border rounded-lg text-white text-sm py-2.5 pl-3 pr-12 focus:outline-none focus:border-[#FF5A1F]/60 transition-colors ${
              prefs.mpg_source === "default" ? "border-[#FF5A1F]/50" : "border-white/10"
            }`}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 text-xs">MPG</span>
        </div>
        {prefs.mpg_source === "default" ? (
          <p className="text-[11px] text-[#FF9764] mt-2 leading-relaxed" data-testid="pricing-mpg-nudge">
            <b className="text-[#FF5A1F]">Set this once</b> — the weekly digest and
            Fuel Cost Estimate will use your real number instead of the Class-8 default (6.5).
            Flatbeds typically run <b className="text-neutral-200">5.5</b>, idle-heavy reefers
            <b className="text-neutral-200"> 4.8-5.5</b>.
          </p>
        ) : (
          <p className="text-[11px] text-neutral-500 mt-2">
            Powers the Fuel Cost Estimate on new loads + the weekly business digest.
            Class-8 tractor average is <b className="text-neutral-300">6.5</b>. Flatbeds
            typically run <b className="text-neutral-300">5.5</b>, idle-heavy reefers
            <b className="text-neutral-300"> 4.8-5.5</b>.
          </p>
        )}
      </div>

      {/* Aggressiveness */}
      <div className="card-surface p-6" data-testid="pricing-aggressiveness-card">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">Negotiation Style</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {AGGRESSIVENESS_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const active = prefs.aggressiveness === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setPrefs({ ...prefs, aggressiveness: opt.value })}
                data-testid={`pricing-aggr-${opt.value}`}
                className={`text-left p-4 rounded-lg border transition-all ${
                  active
                    ? "border-[#FF5A1F]/60 bg-[#FF5A1F]/[0.08] ring-1 ring-[#FF5A1F]/30"
                    : "border-white/10 hover:border-white/25"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={16} weight="fill" className={opt.color} />
                  <span className="text-white font-semibold text-sm">{opt.label}</span>
                  {active && <CheckCircle size={12} weight="fill" className="text-[#FF5A1F] ml-auto" />}
                </div>
                <p className="text-[11px] text-neutral-400 leading-relaxed">{opt.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Speculation Mode — advanced setting for aggressive bidders */}
      <div className="card-surface p-6" data-testid="pricing-speculation-card">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">Bidding Behavior</div>
        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={!!prefs.speculation_mode}
              onChange={(e) => setPrefs({ ...prefs, speculation_mode: e.target.checked })}
              data-testid="pricing-speculation-toggle"
              className="peer sr-only"
            />
            <div className="w-10 h-6 rounded-full bg-white/10 peer-checked:bg-[#FF5A1F] transition-colors" />
            <div className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white font-medium">Speculation Mode</div>
            <p className="text-[11px] text-neutral-400 mt-1 leading-relaxed">
              When enabled, bids that auto-close after 7 days with no broker response
              stay <b className="text-neutral-300">excluded from your win rate</b> (marked as
              "no data" rather than losses). Turn this on if you bid aggressively /
              speculatively and don't want ghost bids to drag your win rate down.
              Auto-closed bids still move out of the Pending bucket either way.
            </p>
          </div>
        </label>
      </div>

      {/* Signature */}
      <div className="card-surface p-6" data-testid="pricing-signature-card">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">Email Signature (auto-added)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-neutral-400 mb-1">Full name</label>
            <div className="relative">
              <User size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                type="text"
                value={prefs.signature_name}
                onChange={set("signature_name")}
                placeholder="MIKELE MANENE"
                data-testid="pricing-signature-name"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 pl-9 pr-3 focus:outline-none focus:border-[#FF5A1F]/60"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-neutral-400 mb-1">Phone</label>
            <div className="relative">
              <Phone size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                type="tel"
                value={prefs.phone}
                onChange={set("phone")}
                placeholder="(555) 555-5555"
                data-testid="pricing-phone"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 pl-9 pr-3 focus:outline-none focus:border-[#FF5A1F]/60"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-neutral-400 mb-1">MC Number</label>
            <div className="relative">
              <IdentificationCard size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                type="text"
                value={prefs.mc_number}
                onChange={set("mc_number")}
                placeholder="1234567"
                data-testid="pricing-mc"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 pl-9 pr-3 focus:outline-none focus:border-[#FF5A1F]/60 font-mono"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-neutral-400 mb-1">DOT Number</label>
            <div className="relative">
              <IdentificationCard size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                type="text"
                value={prefs.dot_number}
                onChange={set("dot_number")}
                placeholder="7654321"
                data-testid="pricing-dot"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 pl-9 pr-3 focus:outline-none focus:border-[#FF5A1F]/60 font-mono"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="pricing-save-btn"
          className="rounded-full px-6 py-3 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 transition-colors inline-flex items-center gap-2"
        >
          {saving ? "Saving…" : (<><CheckCircle size={14} weight="fill" /> Save preferences</>)}
        </button>
      </div>

      {/* Slack Digest Fallback */}
      <SlackWebhookSection />
    </div>
  );
}
